import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import DataTable from "./DataTable";
import { formatCurrency } from "../utils/formatters";
import { getAuthHeaders } from "../../../../utils/auth";
import { buildDateRangeParams } from "../utils/dateRange";

const EXCLUDED_REASON_LABELS = {
  no_signups: "Brak zapisów",
  cancelled: "Odwołane",
  not_yet_held: "Jeszcze się nie odbyły",
};

// One line per rate bucket, same as the parent summary row.
const RatesCell = ({ row }) => {
  if (row.rates.length === 1 && row.rates[0].category_name === null) {
    return (
      <Typography variant="body2">
        {row.has_missing_rate ? "-" : formatCurrency(row.rates[0].rate)}
      </Typography>
    );
  }
  return (
    <Box>
      {row.rates.map((rate, index) => (
        <Typography key={index} variant="body2">
          {rate.category_name ?? "domyślna"}: {formatCurrency(rate.rate)} × {rate.lesson_count}
        </Typography>
      ))}
    </Box>
  );
};

const TrainerAccordionRow = ({ row, dateFrom, dateTo, isMobile }) => {
  const [expanded, setExpanded] = useState(false);
  const [lessons, setLessons] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = async (_, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && lessons === null && !loading) {
      setLoading(true);
      setError("");
      try {
        const headers = await getAuthHeaders();
        const params = buildDateRangeParams(dateFrom, dateTo);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainer-payouts/${row.trainer_id}/lessons?${params.toString()}`,
          { headers }
        );
        setLessons(response.data.lessons);
      } catch (err) {
        setError("Nie udało się załadować listy zajęć");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Accordion expanded={expanded} onChange={handleChange} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1, minWidth: 120 }}>
            {row.trainer_name}
          </Typography>
          {row.has_missing_rate && <Chip label="brak stawki" color="warning" size="small" />}
          <Chip label={`${row.lesson_count} zajęć`} size="small" variant="outlined" />
          <Box sx={{ ml: "auto", display: "flex", gap: 2, alignItems: "center" }}>
            <RatesCell row={row} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#11998e" }}>
              {formatCurrency(row.total_payout)}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && lessons && (
          <DataTable
            size={isMobile ? "small" : "medium"}
            rows={lessons}
            getRowKey={(lesson) => lesson.lesson_id}
            emptyMessage="Brak zajęć w wybranym okresie"
            columns={[
              {
                key: "start",
                label: "Data",
                render: (lesson) => (
                  <Typography variant="body2">
                    {new Date(lesson.start).toLocaleString("pl-PL", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                ),
              },
              {
                key: "lesson_type_name",
                label: "Zajęcia",
                render: (lesson) => <Typography variant="body2">{lesson.lesson_type_name}</Typography>,
              },
              {
                key: "signup_count",
                label: "Uczestnicy",
                align: "right",
                render: (lesson) => <Typography variant="body2">{lesson.signup_count}</Typography>,
              },
              {
                key: "amount",
                label: "Kwota / Powód pominięcia",
                align: "right",
                render: (lesson) =>
                  lesson.counted ? (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#11998e" }}>
                      {formatCurrency(lesson.rate)}
                    </Typography>
                  ) : (
                    <Chip
                      label={EXCLUDED_REASON_LABELS[lesson.excluded_reason] || lesson.excluded_reason}
                      size="small"
                      color={lesson.excluded_reason === "cancelled" ? "error" : "default"}
                      variant="outlined"
                    />
                  ),
              },
            ]}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default TrainerAccordionRow;
